import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext';
// RBAC removed - no permission imports needed
import PageHandler from './PageHandler';
import NewCustomButton from './common/NewCustomButton';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { FEATURES } from '../features';
import '../styles/Sidebar.css';
import {
  FaHome,
  FaBuilding,
  FaUsers,
  FaTools,
  FaPoundSign,
  FaChartLine,
  FaUserTie,
  FaCog,
  FaAngleRight,
  FaLink,
  FaArchive,
  FaShieldAlt,
  FaBullhorn,
  FaClipboardCheck,
  FaCalendar,
  FaFlask,
  FaFileAlt,
  FaTimes
} from 'react-icons/fa';

// Define menu items outside the component to prevent recreation on each render
const menuItems = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    icon: FaHome,
    pageName: 'dashboard',
    requiredFeature: 'viewDashboard'
  },
  {
    title: 'Properties',
    path: '/properties',
    icon: FaBuilding,
    pageName: 'properties',
    requiredFeature: 'viewProperties'
  },
  {
    title: 'Tenants',
    path: '/tenants',
    icon: FaUsers,
    pageName: 'tenants',
    requiredFeature: 'viewTenants'
  },
  {
    title: 'Landlords',
    path: '/landlords',
    icon: FaUserTie,
    pageName: 'landlords',
    requiredFeature: 'viewLandlords'
  },
  {
    title: 'Compliance',
    path: '/compliance',
    icon: FaShieldAlt,
    pageName: 'properties',
    requiredFeature: 'viewProperties'
  },
  {
    title: 'Maintenance',
    path: '/maintenance',
    icon: FaTools,
    pageName: 'maintenance',
    requiredFeature: 'viewMaintenance'
  },
  {
    title: 'Inspections',
    path: '/inspections',
    icon: FaClipboardCheck,
    pageName: 'inspections',
    requiredFeature: 'viewInspections'
  },
  {
    title: 'Calendar',
    path: '/calendar',
    icon: FaCalendar,
    pageName: 'calendar',
    requiredFeature: 'viewCalendar'
  },
  {
    title: 'Finance',
    path: '/finance',
    icon: FaPoundSign,
    pageName: 'finance',
    requiredFeature: 'viewFinance'
  },
  {
    title: 'Reports',
    path: '/reports',
    icon: FaChartLine,
    pageName: 'reports',
    requiredFeature: 'viewReports'
  },
  {
    title: 'Contractors',
    path: '/contractors',
    icon: FaTools,
    pageName: 'contractors',
    requiredFeature: 'viewContractors'
  },
  {
    title: 'Links',
    path: '/links',
    icon: FaLink,
    pageName: 'links',
    requiredFeature: 'viewLinks'
  },
  {
    title: 'Company Docs',
    path: '/company-documents',
    icon: FaFileAlt,
    pageName: 'companyDocuments',
    requiredFeature: 'viewDocuments'
  },
  {
    title: 'Broadcast',
    path: '/broadcast',
    icon: FaBullhorn,
    pageName: 'broadcast',
    requiredFeature: 'sendBroadcasts'
  },
  {
    title: 'Archive',
    path: '/archive',
    icon: FaArchive,
    pageName: 'archive',
    requiredFeature: 'viewArchive',
    adminOnly: true
  },
  {
    title: 'TEST',
    path: '/test',
    icon: FaFlask,
    pageName: 'test',
    requiredFeature: null,
    administratorOnly: true
  },
  {
    title: 'Settings',
    path: '/settings',
    icon: FaCog,
    pageName: 'settings',
    requiredFeature: 'accessSettings'
  }
];

const MOBILE_BREAKPOINT = 768;
const TABLET_MAX = 1024;

const Sidebar = () => {
  const { isMobileOpen, toggleSidebar } = useSidebar();
  const { isEnabled: isFeatureEnabled, hubFlags, localFlags } = useFeatureFlags();
  const { isDarkMode, primaryColor, secondaryColor } = useTheme();
  const { userRole, userPermissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: authLogout } = useAuth();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }
    return false;
  });
  const [isTablet, setIsTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      const w = window.innerWidth;
      return w >= MOBILE_BREAKPOINT && w < TABLET_MAX;
    }
    return false;
  });

  const isDrawerMode = isMobile || isTablet;

  const resolvedPrimary = primaryColor || '#4d8063';
  const resolvedSecondary = secondaryColor || '#1F1F1F';

  useEffect(() => {
    const updateState = () => {
      if (typeof window === 'undefined') return;
      const w = window.innerWidth;
      setIsMobile(w < MOBILE_BREAKPOINT);
      setIsTablet(w >= MOBILE_BREAKPOINT && w < TABLET_MAX);
    };

    updateState();
    window.addEventListener('resize', updateState);
    return () => window.removeEventListener('resize', updateState);
  }, []);

  // Filter menu items based on role permissions and feature flags
  const filteredMenuItems = useMemo(() => {
    if (!userPermissions) {
      console.log('⏳ Sidebar: No permissions loaded yet');
      return [];
    }

    // Administrators and Owners see everything (but still respect feature flags)
    const normalizedUserRole = userRole?.toLowerCase();
    const isAdminOrOwner = normalizedUserRole === 'administrator' || normalizedUserRole === 'owner';
    
    const baseItems = isAdminOrOwner
      ? menuItems 
      : menuItems.filter(item => {
          // Check if user has access to this page
          const hasPageAccess = userPermissions?.pages?.[item.pageName] === true;
          
          // If item has a requiredFeature, also check feature permission
          let hasFeatureAccess = true;
          if (item.requiredFeature) {
            hasFeatureAccess = userPermissions?.features?.[item.requiredFeature] === true;
          }
          
          const hasAccess = hasPageAccess && hasFeatureAccess;
          
          if (!hasAccess) {
            if (!hasPageAccess) {
              console.log(`🚫 Sidebar: Hiding ${item.title} (${item.pageName}) - no page permission`);
            }
            if (item.requiredFeature && !hasFeatureAccess) {
              console.log(`🚫 Sidebar: Hiding ${item.title} - no feature permission for ${item.requiredFeature}`);
            }
          }
          
          return hasAccess;
        });

    // Filter by feature flags and administrator-only items
    const filtered = baseItems.filter(item => {
      // TEST page: only show for Administrator (or "admin") role
      if (item.administratorOnly === true) {
        const isAdministrator = normalizedUserRole === 'administrator' || normalizedUserRole === 'admin';
        if (!isAdministrator) {
          console.log(`🚫 Sidebar: Hiding ${item.title} - administrator only`);
          return false;
        }
      }

      // Check feature flags for Finance and Reports
      if (item.path === '/finance' && !isFeatureEnabled(FEATURES.FINANCE_MODULE)) {
        console.log(`🚫 Sidebar: Hiding ${item.title} - FINANCE_MODULE disabled`);
        return false;
      }
      
      if (item.path === '/reports' && !isFeatureEnabled(FEATURES.FINANCE_MODULE)) {
        console.log(`🚫 Sidebar: Hiding ${item.title} - FINANCE_MODULE disabled (Reports depend on Finance)`);
        return false;
      }
      
      // Check feature flag for Inspections
      if (item.path === '/inspections' && !isFeatureEnabled(FEATURES.INSPECTIONS)) {
        console.log(`🚫 Sidebar: Hiding ${item.title} - INSPECTIONS disabled`);
        return false;
      }
      
      // Add other feature flag checks as needed
      return true;
    });

    console.log(`✅ Sidebar: Showing ${filtered.length}/${menuItems.length} menu items for role: ${userRole}`);
    return filtered;
  }, [userPermissions, userRole, hubFlags, localFlags]);

  const hexToRgba = (hex, alpha) => {
    let h = (hex || '').replace('#', '');
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    const r = parseInt(h.substring(0, 2), 16) || 0;
    const g = parseInt(h.substring(2, 4), 16) || 0;
    const b = parseInt(h.substring(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const sidebarVariables = useMemo(() => ({
    '--sidebar-primary': resolvedPrimary,
    '--sidebar-secondary': resolvedSecondary,
    '--sidebar-surface': isDarkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.99)',
    '--sidebar-border': isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)',
    '--sidebar-text': isDarkMode ? 'rgba(248, 250, 252, 0.96)' : 'rgba(15, 23, 42, 0.95)',
    '--sidebar-muted': isDarkMode ? 'rgba(148, 163, 184, 0.78)' : 'rgba(71, 85, 105, 0.78)',
    '--sidebar-hover': hexToRgba(resolvedPrimary, isDarkMode ? 0.14 : 0.1),
    '--sidebar-active': hexToRgba(resolvedPrimary, isDarkMode ? 0.22 : 0.16),
    '--sidebar-shadow': isDarkMode
      ? '0 18px 40px -20px rgba(15, 23, 42, 0.6)'
      : '0 18px 40px -20px rgba(15, 23, 42, 0.2)'
  }), [resolvedPrimary, resolvedSecondary, isDarkMode]);

  const handleLogout = async () => {
    try {
      if (isDrawerMode && isMobileOpen) {
        toggleSidebar();
      }
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleLinkClick = () => {
    if (isMobileOpen) {
      toggleSidebar();
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const overlayActive = isDrawerMode && isMobileOpen;

  const baseWrapperClass = [
    'sidebar-wrapper',
    isDarkMode ? 'sidebar-wrapper--dark' : 'sidebar-wrapper--light',
    isDrawerMode ? 'sidebar-wrapper--mobile' : ''
  ].filter(Boolean).join(' ');

  const mobileTransform = isDrawerMode
    ? (isMobileOpen ? 'translateX(0)' : 'translateX(-105%)')
    : undefined;

  const getWrapperStyle = () => ({
    transform: mobileTransform,
    pointerEvents: isDrawerMode ? (isMobileOpen ? 'auto' : 'none') : 'auto'
  });

  // RBAC removed - no loading state needed for permissions

  return (
    <>
      {overlayActive && (
        <div
          className="sidebar-overlay active"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <div
        className={baseWrapperClass}
        style={getWrapperStyle()}
        data-mobile={isMobile}
        data-open={isMobileOpen}
        aria-hidden={isDrawerMode ? (!isMobileOpen).toString() : 'false'}
        onClick={(e) => {
          // Prevent clicks inside sidebar from closing it
          e.stopPropagation();
        }}
      >
        <aside className="sidebar-shell" style={sidebarVariables}>
          {isDrawerMode && isMobileOpen && (
            <button
              type="button"
              onClick={toggleSidebar}
              className="sidebar-close-btn"
              aria-label="Close menu"
            >
              <FaTimes size={20} />
            </button>
          )}
          <nav className="sidebar-nav" aria-label="Primary">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <PageHandler
                  key={item.path}
                  path={item.path}
                  onClick={handleLinkClick}
                  className={`sidebar-link ${active ? 'sidebar-link--active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  title={item.title}
                >
                  <Icon className="sidebar-link__icon" aria-hidden="true" />
                  <span className="sidebar-link__label">{item.title}</span>
                  <FaAngleRight className="sidebar-link__chevron" aria-hidden="true" />
                </PageHandler>
              );
            })}
          </nav>

          <footer className="sidebar-footer">
            <div className="sidebar-footer__content">
              <NewCustomButton
                fullWidth
                compact
                onClick={handleLogout}
                variant="secondary"
                color="#EF4444"
              >
                Logout
              </NewCustomButton>
            </div>
          </footer>
        </aside>
      </div>
    </>
  );
};

export default Sidebar;